import { rampFill, rampHatch, inkOnFill } from '@/modules/DataAnalysis/Risk/riskFormat';

export const catalogForChips = (state) => state.catalog;

// The RAW catalog entry, before the direction merge below — op (and threshold) read
// this rather than activeIndicator to avoid a circular getter (activeIndicator itself
// depends on op).
const catalogEntry = (state) => state.catalog.find((entry) => entry.id === state.indicator) || null;

export const op = (state) => {
    const stored = state.opByIndicator[state.indicator];
    if (stored != null) return stored;
    const entry = catalogEntry(state);
    return (entry && entry.defaultOp) || '>';
};

// The single presentation object every component reads (chart, table, summary,
// message card, CSV). For an indicator with per-direction copy (arid's Drought Index
// </> toggle — riskPresentation.js#byOp), this is the ONE place that merges the active
// direction's copy over the base entry, so nothing downstream has to know a toggle
// exists — RiskProbabilityChart/RiskWeeklyTable/RiskPeriodSummary/RiskMessageCard/
// riskCsv.js all keep reading flat presentation.chartTitle/.eventPhrase/.detailColumns.
// The Heat Index level (riskPresentation.js#categorySelectable), read off the raw catalog
// entry for the same reason `op` is: activeIndicator depends on it. null for an
// indicator without levels.
export const category = (state) => {
    const stored = (state.categoryByIndicator || {})[state.indicator];
    if (stored != null) return stored;
    const entry = catalogEntry(state);
    return (entry && entry.defaultCategory) || null;
};

// The minimum daily exposure (N), read like `category`: stored per indicator, falling
// back to the catalog entry's default. null for an indicator without it.
export const minHours = (state) => {
    const stored = (state.minHoursByIndicator || {})[state.indicator];
    if (stored != null) return stored;
    const entry = catalogEntry(state);
    return entry && entry.defaultMinHours != null ? entry.defaultMinHours : null;
};

export const activeIndicator = (state, getters) => {
    const entry = catalogEntry(state);
    if (!entry || (!entry.byOp && !entry.byCategory)) return entry;
    return {
        ...entry,
        ...((entry.byOp && entry.byOp[getters.op]) || {}),
        ...((entry.byCategory && entry.byCategory[getters.category]) || {}),
    };
};

export const threshold = (state, getters) => {
    const stored = state.thresholdByIndicator[state.indicator];
    if (stored != null) return stored;
    return getters.activeIndicator ? getters.activeIndicator.defaultThreshold : null;
};

export const smallSample = (state) =>
    (state.data && state.data.warnings.smallSample) || { active: false, precision: 1 };

// small_sample is deliberately NOT part of viewState — it's a modifier of
// `ready` (hatch + rounding + an orange note), not a distinct screen.
export const viewState = (state, getters, rootState) => {
    const hasPin = !!(rootState.mapLatLon && rootState.mapLatLon.latitude != null);
    if (!hasPin) return 'no_pin';
    // Nothing has been requested yet — the tab follows the position LOAD DATA was last
    // pressed for, so a freshly dropped pin sits here until it is. Without this the
    // fallthrough below would show the loading skeleton forever, waiting on a request
    // that was never made.
    if (state.status === 'idle') return 'no_pin';

    if (state.status === 'error' && state.error) {
        if (state.error.code === 'outside_coverage') return 'outside_coverage';
        if (state.error.code === 'upstream_unavailable') return 'upstream_unavailable';
        if (state.error.code === 'not_implemented') return 'not_implemented';
        return 'error';
    }
    if (state.status === 'empty') return 'no_data';
    if (state.status === 'ready' && state.data) return 'ready';
    return 'loading';
};

// The single source of truth for every displayed number in blocks D and E —
// the chart and the table read the same array so they can never disagree.
export const chartBars = (state, getters) => {
    if (!state.data) return [];
    const small = getters.smallSample;

    return state.data.buckets.map((bucket) => {
        const pctDisplay = bucket.pctDisplay;
        const heightPct = Math.max(0, Math.min(100, pctDisplay == null ? 0 : pctDisplay));
        return {
            key: bucket.start_date,
            label: bucket.label,
            labelShort: bucket.label_short,
            days: bucket.days,
            isPartial: bucket.is_partial,
            isPeak: bucket.is_peak,
            pctRaw: bucket.pctRaw,
            pctDisplay,
            prefix: small.active ? '~' : '',
            heightPct,
            // Ramp keyed on the live selection, not data.indicator: the echo in
            // the payload lags the chips while a request is in flight.
            fill: small.active ? null : rampFill(pctDisplay, state.indicator),
            hatch: small.active ? rampHatch(pctDisplay, state.indicator) : null,
            inkOnFill: inkOnFill(pctDisplay),
            yearsWithEvent: bucket.years_with_event,
            yearsTotal: bucket.years_total,
            raw: bucket,
        };
    });
};

// "HI >= 104°F for >= 6 h/day" — the two-part condition of the heat index indicators,
// built from what the response itself echoed back.
const conditionLineOf = (presentation, data) => {
    if (!presentation || !presentation.categorySelectable || !data.category) return null;
    const level = (presentation.categories || []).find((c) => c.id === data.category);
    if (!level) return null;
    const degrees = String(level.hint || '').match(/(-?\d+)/);
    const intensity = degrees ? `HI ≥ ${degrees[1]}°F` : level.label;
    return data.min_hours != null ? `${intensity} for ≥ ${data.min_hours} h/day` : intensity;
};

export const periodSummary = (state, getters) => {
    if (!state.data) return null;
    const presentation = getters.activeIndicator;
    const small = getters.smallSample;
    const periodMeanField = (presentation && presentation.periodMeanField) || 'mean_chill_hours';
    const periodRecordField = (presentation && presentation.periodRecordField) || 'record_chill_hours';
    return {
        pctDisplay: state.data.period.pctDisplay,
        prefix: small.active ? '~' : '',
        yearsWithEvent: state.data.period.years_with_event,
        yearsTotal: state.data.period.years_total,
        startDate: state.data.period.start_date,
        endDate: state.data.period.end_date,
        peakWeeks: state.data.period.peakWeeks,
        threshold: state.data.threshold,
        units: state.data.units,
        eventPhrase: presentation ? presentation.eventPhrase : '',
        climatologyWindow: state.data.provenance.climatology_window,
        // Only present for a period-only indicator (chill_hours — weekly: false):
        // the API attaches its mean_*/record_* fields to `period`
        // instead of to a bucket, since there is no bucket to attach them to. Which
        // fields those are is per indicator (riskPresentation.js#periodMeanField).
        // null for every other indicator; RiskPeriodSummary renders a line only when set.
        meanTotal: state.data.period[periodMeanField] != null ? state.data.period[periodMeanField] : null,
        recordTotal: state.data.period[periodRecordField] || null,
        totalsLabel: (presentation && presentation.periodTotalsLabel) || 'Mean season total',
        recordLabel: (presentation && presentation.periodRecordLabel) || 'Most',
        // The exposure condition behind every number above, for the indicators that
        // have one — a persistence figure must never be shown without it.
        conditionLine: conditionLineOf(presentation, state.data),
    };
};

export const provenanceLines = (state) => {
    if (!state.data) return [];
    const p = state.data.provenance || {};
    const cw = p.climatology_window || {};
    return [
        [p.source, p.grid].filter(Boolean).join(' / '),
        cw.start_year ? `Climatology: ${cw.start_year}–${cw.end_year} (${cw.n_years} years)` : null,
        // The xclim engine/version line was dropped from the footer on purpose: it is
        // implementation detail for the reader of the tab. It still rides in the CSV
        // header (riskCsv.js), where the reader is auditing an export.
        p.computed_at ? `Computed: ${p.computed_at}` : null,
    ].filter(Boolean);
};

// Bundles what riskCsv.js needs to build the export — kept as a getter so
// the component doesn't have to reach into two different pieces of state.
export const exportPayload = (state, getters) => ({
    data: state.data,
    presentation: getters.activeIndicator,
});
